import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Upload, Save, FileText, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const TemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, accessToken } = useContext(AuthContext);
  
  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/templates/${id}`, { headers });
      setTemplate(response.data);
      setTemplateName(response.data.name);
    } catch (error) {
      toast.error('Failed to load template');
      navigate('/templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      toast.success('PDF selected');
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const headers = { 
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data'
      };

      await axios.post(`${API}/templates/${id}/upload`, formData, { headers });
      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      fetchTemplate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      setIsSaving(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.put(`${API}/templates/${id}`, { name: templateName }, { headers });
      toast.success('Template updated successfully!');
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishTemplate = async () => {
    try {
      setIsSaving(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(`${API}/templates/${id}/publish`, {}, { headers });
      toast.success('Template published successfully!');
      fetchTemplate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.delete(`${API}/templates/${id}`, { headers });
      toast.success('Template deleted successfully!');
      navigate('/templates');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/templates')}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Templates
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 truncate">{template?.name}</h1>
              <p className="text-gray-600 mt-1">
                Status: <span className={`font-medium ${template?.status === 'active' ? 'text-green-600' : 'text-gray-600'}`}>
                  {template?.status}
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {template?.status === 'draft' && (
              <Button
                onClick={handlePublishTemplate}
                disabled={isSaving}
                className="bg-gradient-to-r from-green-600 to-emerald-600"
              >
                <Save size={18} className="mr-2" />
                Publish Template
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={handleDeleteTemplate}
            >
              <Trash2 size={18} className="mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Template Details */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Template Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Template Name</label>
                <Input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                />
              </div>
              {templateName === template?.name && (
                <p className="text-xs text-gray-500">Change the name to enable update</p>
              )}
              <Button
                onClick={handleUpdateTemplate}
                disabled={isSaving || templateName === template?.name}
                className="w-full"
              >
                <Save size={18} className="mr-2" />
                {isSaving ? 'Updating...' : 'Update Template'}
              </Button>
            </CardContent>
          </Card>

          {/* Document Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Upload Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">PDF Document</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                />
                {selectedFile ? (
                  <p className="text-sm text-green-600 mt-2 font-medium">
                    ✓ Selected: {selectedFile.name}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">
                    Select a PDF file to enable upload
                  </p>
                )}
              </div>
              <Button
                onClick={handleUploadDocument}
                disabled={isSaving || !selectedFile}
                className="w-full"
              >
                <Upload size={18} className="mr-2" />
                {isSaving ? 'Uploading...' : 'Upload Document'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Template Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Template Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">{new Date(template?.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Updated At</p>
                <p className="font-medium">{new Date(template?.updated_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Template ID</p>
                <p className="font-mono text-sm">{template?.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium capitalize">{template?.status}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <FileText className="text-blue-600 flex-shrink-0 mt-1" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">Next Steps</h3>
                <ul className="text-sm text-blue-800 space-y-1 mb-3">
                  <li>1. Upload a PDF document for this template</li>
                  <li>2. Add signature fields and form fields using the Field Editor</li>
                  <li>3. Publish the template to make it available for envelopes</li>
                  <li>4. Use this template to create envelopes for signing</li>
                </ul>
                <Button
                  onClick={() => navigate(`/templates/${id}/editor`)}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600"
                >
                  <FileText size={18} className="mr-2" />
                  Open Field Editor
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TemplateDetail;
